import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { RiskPoolOrganization } from '../../_types/risk_pool';
import { primary, neutral } from '_lex/_foundations/phxColors';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { GridCellParams, GridRowParams } from '@mui/x-data-grid-pro';
import { compliancePerformanceColors } from '..';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate } from 'react-router-dom';

type RiskPoolOrganizationsProps = {
  organizations: RiskPoolOrganization[];
  isLoading?: boolean;
  query: {
    isLoading?: boolean;
    error?: any;
    data?: any;
    isFetchingNextPage?: boolean;
    hasNextPage?: boolean;
    fetchNextPage?: () => Promise<any>;
    isError?: boolean;
  };
  totalCount?: number;
  sortOrder?: string;
  updateParams?: (params: Partial<{ sortOrder: string }>) => void;
};

const renderHeaderWithTooltip = (headerText: string, tooltipText: string) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Tooltip title={tooltipText} arrow placement='top'>
      <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
    </Tooltip>
    <Typography variant='bodySm' sx={{ fontWeight: 600 }}>
      {headerText}
    </Typography>
  </Box>
);

export default function RiskPoolOrganizations({
  organizations,
  isLoading,
  query,
  totalCount,
  sortOrder = 'asc',
  updateParams,
}: RiskPoolOrganizationsProps) {
  const navigate = useNavigate();

  const handleRowClick = (params: GridRowParams) => {
    const row = params.row as RiskPoolOrganization;
    if (row?.id) {
      navigate(`/admin/dashboard/risk_pool/${row.id}`);
    }
  };

  const columns: ColumnDef = {
    name: {
      header: t('dashboard.riskPool.grid.organization'),
      flex: 2,
      display: 'flex',
      columnProps: {
        sortable: false,
        renderHeader: () => (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => updateParams?.({ sortOrder: sortOrder === 'asc' ? 'desc' : 'asc' })}
          >
            <Typography variant='bodySm' sx={{ fontWeight: 600 }}>
              {t('dashboard.riskPool.grid.organization')}
            </Typography>
            {sortOrder === 'asc' ? (
              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
            )}
          </Box>
        ),
      },
      renderer: (params: GridCellParams) => {
        const row = params.row as RiskPoolOrganization;
        if (!row) return null;
        return (
          <Typography sx={{ color: primary[800], textDecoration: 'underline' }}>
            {row.name}
          </Typography>
        );
      },
    },
    compliance: {
      header: t('dashboard.riskPool.grid.compliance'),
      flex: 1,
      display: 'flex',
      columnProps: {
        renderHeader: () =>
          renderHeaderWithTooltip(
            t('dashboard.riskPool.grid.compliance'),
            t('dashboard.riskPool.grid.complianceTooltip'),
          ),
        colSpan: (_value: any, row: any) => (!row.hasManualContent ? 3 : 1),
      },
      renderer: (params: GridCellParams) => {
        const row = params.row as RiskPoolOrganization;
        if (!row) return null;
        if (!row.hasManualContent) {
          return (
            <Typography variant='bodySm' sx={{ fontStyle: 'italic', width: '100%', textAlign: 'center' }}>
              {t('dashboard.riskPool.grid.noManualContent')}
            </Typography>
          );
        }
        return (
          <Box
            sx={{
              borderRadius: '50%',
              width: 12,
              height: 12,
              backgroundColor: compliancePerformanceColors[row.compliancePerformance],
              flexShrink: 0,
            }}
          />
        );
      },
    },
    issuedPolicies: {
      header: t('dashboard.riskPool.grid.issuedPolicies'),
      flex: 1,
      display: 'flex',
      columnProps: {
        renderHeader: () =>
          renderHeaderWithTooltip(
            t('dashboard.riskPool.grid.issuedPolicies'),
            t('dashboard.riskPool.grid.issuedPoliciesTooltip'),
          ),
      },
      renderer: (params: GridCellParams) => {
        const row = params.row as RiskPoolOrganization;
        if (!row || !row.hasManualContent) return null;
        return (
          <Typography variant='bodySm'>
            {row.issuedPolicyCount}/{row.draftPolicyCount}
          </Typography>
        );
      },
    },
    modifiedPercentage: {
      header: t('dashboard.riskPool.grid.modifiedPercentage'),
      flex: 1,
      display: 'flex',
      renderer: (params: GridCellParams) => {
        const row = params.row as RiskPoolOrganization;
        if (!row || !row.hasManualContent) return null;
        return <Typography variant='bodySm'>{Math.round(row.modifiedPolicyPercentage * 100)}%</Typography>;
      },
    },
    chevron: {
      header: '',
      flex: 0,
      display: 'flex',
      columnProps: {
        width: 48,
        minWidth: 48,
        sortable: false,
        renderHeader: () => null,
      },
      renderer: () => <ChevronRightIcon sx={{ color: neutral[400], fontSize: 20 }} />,
    },
  };

  return (
    <Box sx={{ minHeight: '50vh' }}>
      <GridSection
        columns={columns}
        data={organizations}
        query={query}
        totalCount={totalCount}
        isLoading={isLoading}
        gridProps={{
          onRowClick: handleRowClick,
          sx: { '& .MuiDataGrid-row': { cursor: 'pointer' } },
        }}
      />
    </Box>
  );
}
